/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import { Typography2 } from '@coursera/cds-core';

import CourseEmailPreferences from 'bundles/account-settings/components/sections/email-preferences/CourseEmailPreferences';
import CourseraEmailPreferences from 'bundles/account-settings/components/sections/email-preferences/CourseraEmailPreferences';
import GlobalEmailNotificationPreference from 'bundles/account-settings/components/sections/email-preferences/GlobalEmailNotificationPreference';
import GlobalPushNotificationPreference from 'bundles/account-settings/components/sections/email-preferences/GlobalPushNotificationPreference';
import NotificationPreferencesTOS from 'bundles/account-settings/components/sections/email-preferences/NotificationPreferencesTOS';
import PartnerEmailPreferences from 'bundles/account-settings/components/sections/email-preferences/PartnerEmailPreferences';
import { isMarketingPushNotificationFieldEnabledForCommunicationPreferences } from 'bundles/account-settings/epicFeatureFlags';
import type {
  CourseEmailPreference,
  GlobalChannelNotificationPreferences,
  PartnerEmailPreference,
  UpdateStatus,
} from 'bundles/account-settings/types/EmailPreferences';
import { NotificationPreferenceChannels } from 'bundles/account-settings/types/EmailPreferences';
import { composeGlobalNotificationPreferencesByChannel } from 'bundles/account-settings/utils/notificationPreferencesHelpers';

import _t from 'i18n!nls/account-settings';

import 'css!./__styles__/EmailPreferences';

type Props = {
  coursePreferences: CourseEmailPreference[];
  partnerPreferences: PartnerEmailPreference[];
  courseraPreferences: GlobalChannelNotificationPreferences;
  courseUpdateStatus: UpdateStatus;
  partnerUpdateStatus: UpdateStatus;
  courseraUpdateStatus: UpdateStatus;
  globalNotificationsEnabled: boolean;
};

const EmailPreferences: React.FC<Props> = ({
  coursePreferences,
  partnerPreferences,
  courseUpdateStatus,
  courseraPreferences,
  courseraUpdateStatus,
  partnerUpdateStatus,
  globalNotificationsEnabled,
}) => {
  // this shows a checkbox for learners to change their push notification preferences for through web
  const showMarketingPushNotifFieldOnCommunicationPreferencePage =
    isMarketingPushNotificationFieldEnabledForCommunicationPreferences();
  const [globalOptOutEnabled, setGlobalOptOutEnabled] = useState(!globalNotificationsEnabled);

  const hasCoursePreferences = coursePreferences?.length > 0;
  const hasPartnerPreferences = partnerPreferences?.length > 0;

  const courseraEmailPreferences = composeGlobalNotificationPreferencesByChannel(
    courseraPreferences,
    NotificationPreferenceChannels.EMAIL
  );
  const courseraPushPreferences = composeGlobalNotificationPreferencesByChannel(
    courseraPreferences,
    NotificationPreferenceChannels.PUSH
  );

  return (
    <div className="rc-EmailPreferences">
      <Typography2 component="h1" variant="titleMedium" className="top-section-title" id="a11y-navigation-heading">
        {_t('Communication Preferences')}
      </Typography2>

      {showMarketingPushNotifFieldOnCommunicationPreferencePage && (
        <div className="preferences-section">
          <GlobalPushNotificationPreference preferences={courseraPushPreferences} />
        </div>
      )}

      <div className="preferences-section">
        <div className="preferences-group">
          <GlobalEmailNotificationPreference
            preference={!globalNotificationsEnabled}
            onChange={(value) => setGlobalOptOutEnabled(value)}
          />
        </div>

        <div className="preferences-group">
          <CourseraEmailPreferences
            disabled={globalOptOutEnabled}
            preferences={courseraEmailPreferences}
            updateStatus={courseraUpdateStatus}
          />
        </div>

        {hasCoursePreferences && (
          <div className="preferences-group">
            <CourseEmailPreferences
              disabled={globalOptOutEnabled}
              updateStatus={courseUpdateStatus}
              coursePreferences={coursePreferences}
            />
          </div>
        )}
        {hasPartnerPreferences && (
          <div className="preferences-group">
            <PartnerEmailPreferences
              disabled={globalOptOutEnabled}
              updateStatus={partnerUpdateStatus}
              partnerPreferences={partnerPreferences}
            />
          </div>
        )}
      </div>

      <div className="preferences-section">
        <NotificationPreferencesTOS />
      </div>
    </div>
  );
};

export default EmailPreferences;
